/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { EditorState, Plugin } from 'prosemirror-state'

import { Decoration, DecorationSet, EditorView } from 'prosemirror-view'

import { _applyDragMove, isColumnSwapOperation, wouldResultInSingleColumn } from 'common/Prose/Commands/columns'
import {
  isDroppingNonListItemInsideList,
  isListItemChangeDepthOperation,
} from 'common/Prose/Commands/columns/list_item'
import { isListItem } from 'common/Prose/utils/isListItem'
import { clamp, inRange, throttle } from 'lodash'
import { ExtractStrongNodeSpecAttributes, nodes, schema } from 'prose-core'
import { ResolvedPos } from 'prosemirror-model'
import { Direction, clampCoordToRect, getClosestSide } from 'utils/coordUtils'
import { HeapEventType, trackEventInHeap } from 'utils/heapTracking'
import styles from './AlmanacDropCursor.module.scss'
import { ALMANAC_DROP_CURSOR_PLUGIN_KEY } from './constants'

export type AlmanacDropCursorPluginState = {
  isDragging: boolean
  dropPoint?: number
  direction?: Direction
  nodeRect?: DOMRect
  draggedNodePos?: number
  targetElementClone?: HTMLElement
  mouseX?: number
}

export type AlmanacDropCursorPluginMeta =
  | ({ action: 'startDrag' } & Pick<AlmanacDropCursorPluginState, 'targetElementClone'> &
      Pick<Required<AlmanacDropCursorPluginState>, 'draggedNodePos'>)
  | { action: 'stopDrag' }
  | ({
      action: 'updateDropPoint'
    } & Pick<Required<AlmanacDropCursorPluginState>, 'dropPoint' | 'direction' | 'nodeRect' | 'mouseX'> &
      Pick<AlmanacDropCursorPluginState, 'targetElementClone'>)
  | ({ action: 'updateMousePosOnly' } & Pick<Required<AlmanacDropCursorPluginState>, 'mouseX'>)

export const createDropPointDecoEl =
  (
    direction: Required<AlmanacDropCursorPluginState>['direction'],
    nodeRect: Pick<DOMRect, 'width' | 'height'>,
    options?: { class?: string; drawAsFullColumn?: boolean; depth?: number },
  ) =>
  () => {
    const el = document.createElement('div')
    const directionMap = ['top', 'right', 'bottom', 'left'] as const
    el.classList.add(styles.DropPoint)
    el.classList.add(styles[directionMap[direction]])
    if (options?.depth) {
      el.dataset.depth = options?.depth.toString()
    }
    el.dataset.direction = directionMap[direction]
    if (options?.class) el.classList.add(options.class)
    if (options?.drawAsFullColumn) {
      el.style.setProperty('--size', '100%')
    } else {
      el.style.setProperty(
        '--size',
        `${[Direction.left, Direction.right].includes(direction) ? nodeRect.height : nodeRect.width}px`,
      )
    }

    el.style.setProperty(
      '--offset',
      `${[Direction.left, Direction.right].includes(direction) ? nodeRect.width : nodeRect.height}px`,
    )

    return el
  }

export const handleDragMove = throttle(
  (view: EditorView, event: MouseEvent) => {
    const {
      state: { tr, doc },
      dispatch,
    } = view
    let target: Element
    const pmRect = view.dom.getBoundingClientRect()
    // we want the height of the area of .ProseMirror that has content, not including the extra white space at the bottom
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const pmContentHeight = view.dom.lastElementChild!.getBoundingClientRect().bottom - pmRect.top

    const [x, y] = clampCoordToRect([event.clientX, event.clientY], {
      top: pmRect.top,
      left: pmRect.left,
      width: pmRect.width,
      height: pmContentHeight,
    })
    /**
     * if the mouse is over some content, set target to be said content
     * if not (e.g. mouse is floating over the margins), get the closest content piece by using projected coordinates
     */
    // `instanceof Element` instead of `instanceof HTMLElement` because an `SVGElement` is acceptable as well
    if (event.target instanceof Element && view.dom.contains(event.target) && event.target !== view.dom) {
      target = event.target
    } else {
      target = document.elementFromPoint(x, y)! ?? event.target
      /**
       * Column groups, columns, and the prosemirror dom all overlap on their left and right edges.
       * So, `elementFromPoint()` will resolve to the inner-most node and we need to traverse upward to
       * find the column group.
       */
      target = target.closest('[data-node-type="column_group"]') ?? target
      if (!view.dom.contains(target)) return
    }

    const pluginState: AlmanacDropCursorPluginState = ALMANAC_DROP_CURSOR_PLUGIN_KEY.getState(view.state)

    const targetTopLevelEl = Array.from(
      // get all immediate children of .ProseMirror
      // and also all inner children of columns
      // and also columns themselves
      // and also direct children of lists inside columns (list_items)
      // and also list items
      view.dom.querySelectorAll(
        `
        :scope>*,
        :scope>[data-node-type="column_group"] [data-type="column"],
        :scope>[data-node-type="column_group"] [data-type="column"]>*,
        :scope>[data-node-type="column_group"] [data-type="column"]>*:is([data-type="ordered_list"],
                                                                          [data-type="unordered_list"],
                                                                          [data-type="check_list"],
                                                                          [data-type="vote_list"])>li,
        :scope>*:is([data-type="ordered_list"],
                    [data-type="unordered_list"],
                    [data-type="check_list"],
                    [data-type="vote_list"])>li
        `,
      ),
    ).find((el) => {
      // a widget can appear on the top level but is not a valid drop target. Skip it.
      if (el.classList.contains('ProseMirror-widget')) return false
      /**
       * If the target is a column (like for example when dropping on an empty space on the bottom of a column),
       * then consider the target element to be the the last element in that column. We can't simply return said element
       * because we are inside `find` so we have to match the element that corresponds to that instead
       */
      if (target instanceof HTMLElement && target.dataset.type === 'column' && el === target.lastElementChild) {
        return true
      }

      // if the element we're checking is a list:
      if (target instanceof HTMLElement && ['UL', 'OL'].includes(el.nodeName)) {
        // we're not interested in the list itself, only its children
        return false
      }

      // if the target is the child of a column then we return that child, not the column itself
      if (target instanceof HTMLElement && target.closest('[data-type="column"]')) {
        return (
          !el.querySelector('[data-type="column"]') &&
          el.contains(target) &&
          el instanceof HTMLElement &&
          el.dataset.type !== 'column'
        )
      }

      // otherwise it's a normal top-level node and thus we return it
      return el.contains(target as Node)
    })

    if (!targetTopLevelEl) {
      dispatch(
        tr.setMeta(ALMANAC_DROP_CURSOR_PLUGIN_KEY, {
          action: 'updateMousePosOnly',
          mouseX: x,
        } as AlmanacDropCursorPluginMeta),
      )
      return
    }

    const targetTopLevelPos = view.posAtDOM(targetTopLevelEl, 0) - (targetTopLevelEl.nodeName === 'LI' ? 1 : 0)
    if (targetTopLevelPos === -1) return
    const $hoveredNodePos = doc.resolve(targetTopLevelPos)
    let targetTopLevelElRect = targetTopLevelEl?.getBoundingClientRect()

    let hoveredNodePos: number
    let direction: Direction
    if (targetTopLevelEl.parentElement?.dataset.type === 'column') {
      direction = getClosestSide([Infinity, y], targetTopLevelElRect)
      /**
       * Weird prosemirror quirk. If you `view.posAtDOM(nodeWithContent, 0)`, you get the position of the node, but if
       * you `view.posAtDOM(leafNode, 0)` you get the position BEFORE the leaf node.
       */
      if ($hoveredNodePos.node().type === schema.nodes.column && $hoveredNodePos.nodeAfter?.isLeaf) {
        hoveredNodePos = $hoveredNodePos.pos
      } else {
        hoveredNodePos = $hoveredNodePos.before()
      }
    } else if (
      targetTopLevelEl instanceof HTMLElement &&
      targetTopLevelEl.dataset.nodeType === 'column_group' &&
      targetTopLevelEl.querySelectorAll('[data-type="column"]').length === 2
    ) {
      /**
       * We are hovering over the column_group, but not over a column, and the column_group has 2 columns
       * This means we are either between columns or to the left/right of the column_group
       */
      /**
       * 1. Create two rects:
       *  1.1. Of the whole column group
       *  1.2. A "virtual rect" representing the gap between the columns
       * 2. Compute the closest side for both rects
       */
      const columnGroupRect = targetTopLevelElRect
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const firstColumnRect = targetTopLevelEl.querySelector('[data-type="column"]')!.getBoundingClientRect()
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const secondColumnRect = targetTopLevelEl.querySelectorAll('[data-type="column"]')![1].getBoundingClientRect()

      const columnGroupDirection = getClosestSide([x, y], columnGroupRect)
      const gapDirection = getClosestSide([x, y], {
        top: columnGroupRect.top,
        bottom: columnGroupRect.bottom,
        left: firstColumnRect.right,
        right: secondColumnRect.left,
      })
      /**
       *  2.1. If they agree on the closest side, that means either top or bottom, so we drop above/below the
       *       column_group
       *  2.2. If the column_group says "left" or "right" then it doesn't matter what the virtual rect says, and we drop
       *       to the left/right of the column_group (this will create a third column)
       */
      if (columnGroupDirection === gapDirection || [Direction.left, Direction.right].includes(columnGroupDirection)) {
        direction = columnGroupDirection
        hoveredNodePos = $hoveredNodePos.before(1)
      } else {
        /**
         *  2.3. Here, column_group says "top" or "bottom", and the virtual column says "left" or "right". In this case
         *       the virtual rect "wins". So we reset the target to be the first column and arbitrarily set the
         *       direction to "right": right of first col or left of second col mean the same thing.
         *
         *       We also need to force the meta rect to the size of the first column.
         */
        direction = Direction.right
        hoveredNodePos = $hoveredNodePos.pos
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        targetTopLevelElRect = targetTopLevelEl.querySelector('[data-type="column"]')!.getBoundingClientRect()
      }
    } else if (
      targetTopLevelEl instanceof HTMLElement &&
      targetTopLevelEl.dataset.nodeType === 'column_group' &&
      targetTopLevelEl.querySelectorAll('[data-type="column"]').length === 3
    ) {
      const columnGroupRect = targetTopLevelElRect
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const columnEls = Array.from(targetTopLevelEl.querySelectorAll('[data-type="column"]')!)
      const colRects = columnEls.map((colEl) => colEl.getBoundingClientRect())
      const colDirections = colRects.map((colRect) => getClosestSide([x, y], colRect)) as [
        Direction,
        Direction,
        Direction,
      ]
      const [a, b, c] = colDirections

      if (a === Direction.right || b === Direction.left) {
        // between A and B
        direction = Direction.right
        hoveredNodePos = view.posAtDOM(columnEls[0], 0) - 1
        targetTopLevelElRect = colRects[0] // rect of A
      } else if (b === Direction.right || c === Direction.left) {
        // between B and C
        direction = Direction.right
        hoveredNodePos = view.posAtDOM(columnEls[1], 0) - 1
        targetTopLevelElRect = colRects[1] // rect of B
      } else if (a === Direction.left) {
        direction = Direction.left
        hoveredNodePos = view.posAtDOM(columnEls[0], 0) - 1
        targetTopLevelElRect = colRects[0] // rect of A
        // before A
      } else if (c === Direction.right) {
        direction = Direction.right
        hoveredNodePos = $hoveredNodePos.pos
        hoveredNodePos = view.posAtDOM(columnEls[2], 0) - 1
        targetTopLevelElRect = colRects[2] // rect of C
        // after C
      } else {
        // not trying to drop between columns (so either above or below)
        direction = getClosestSide([x, y], columnGroupRect)
        hoveredNodePos = $hoveredNodePos.before(1)
      }
    } else {
      direction = (() => {
        const leftDistance = x - event.clientX // distance from mouse to left edge of target
        if (pluginState.draggedNodePos === $hoveredNodePos.pos || inRange(leftDistance, 0, 32)) {
          // We make it so that only TOP or BOTTOM are possible if:
          //   - we are hovering over the node we are dragging (you can't side stack a node with
          //     itself)
          //   - we are hovering over the left 32px of the node (this is a heuristic to make it
          //     easier to drop a node into a list without accidentally side stacking it)
          return getClosestSide([Infinity, y], targetTopLevelElRect)
        } else {
          // Otherwise, we consider all four sides based on the mouse position
          return getClosestSide([x, y], targetTopLevelElRect)
        }
      })()
      if (isListItem($hoveredNodePos.nodeAfter?.type) && pluginState.draggedNodePos !== undefined) {
        // If we are hovering over a list item, we want the position to point to the list item
        hoveredNodePos = $hoveredNodePos.pos
      } else {
        // Otherwise, we want the position to point to the top-level node (depth 1, or immediate
        // child of doc)
        hoveredNodePos = $hoveredNodePos.before(1)
      }
    }

    const meta: AlmanacDropCursorPluginMeta = {
      action: 'updateDropPoint',
      dropPoint: hoveredNodePos,
      direction,
      nodeRect: targetTopLevelElRect,
      mouseX: x,
    }
    if (!pluginState.targetElementClone && pluginState.draggedNodePos !== undefined) {
      const draggedEl = view.domAtPos(pluginState.draggedNodePos + 1).node as HTMLElement
      const clone = draggedEl.cloneNode(true) as HTMLElement
      Array.from(clone.querySelectorAll('.ProseMirror-widget')).forEach((el) => el.remove())
      clone.id = 'dragClone'
      meta.targetElementClone = clone
    }

    dispatch(tr.setMeta(ALMANAC_DROP_CURSOR_PLUGIN_KEY, meta))
  },
  100,
  { trailing: true },
)

enum DropTargetType {
  valid = 'valid',
  invalid = 'invalid',
  hidden = 'hidden',
}

export const isValidDropTarget = (
  $dragPos: ResolvedPos,
  $dropPos: ResolvedPos,
  direction: Required<AlmanacDropCursorPluginState>['direction'],
  depth?: number,
): DropTargetType => {
  let result = DropTargetType.valid
  const _isListItemChangeDepthOperation =
    depth !== undefined && isListItemChangeDepthOperation(direction, $dragPos, $dropPos, depth)
  if ($dragPos === $dropPos && !_isListItemChangeDepthOperation) result = DropTargetType.hidden
  switch (direction) {
    case Direction.top:
      // dropping above the same node, without changing depth
      if ($dragPos.pos + ($dragPos.nodeAfter?.nodeSize ?? 0) === $dropPos.pos && !_isListItemChangeDepthOperation)
        result = DropTargetType.hidden
      break
    case Direction.bottom:
      // dropping below the same node, without changing depth
      if ($dropPos.pos + ($dropPos.nodeAfter?.nodeSize ?? 0) === $dragPos.pos && !_isListItemChangeDepthOperation)
        result = DropTargetType.hidden
      break
    case Direction.left:
    case Direction.right:
      if (isColumnSwapOperation($dragPos, $dropPos)) {
        // swapping columns
        result = DropTargetType.valid
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      } else if ($dropPos.nodeAfter!.type === schema.nodes.column && $dropPos.node(1).childCount === 3) {
        // dropping to the left/right of a column in a column group that already has 3 columns
        result = DropTargetType.invalid
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      } else if ($dropPos.nodeAfter!.type === schema.nodes.column_group && $dropPos.nodeAfter!.childCount === 3) {
        // dropping to the left/right of a column group that already has 3 columns
        result = DropTargetType.invalid
      }
      break
  }
  return result
}

export const handleDragEnd = (view: EditorView) => {
  const {
    dispatch,
    state,
    state: { tr },
  } = view
  const meta: AlmanacDropCursorPluginMeta = {
    action: 'stopDrag',
  }
  let { draggedNodePos, dropPoint, direction, mouseX, nodeRect } = ALMANAC_DROP_CURSOR_PLUGIN_KEY.getState(
    state,
  ) as AlmanacDropCursorPluginState

  if (draggedNodePos === undefined || dropPoint === undefined || direction === undefined) {
    if (tr !== undefined) {
      dispatch(tr.setMeta(ALMANAC_DROP_CURSOR_PLUGIN_KEY, meta))
    }
    return
  }

  const $draggedPos = tr.doc.resolve(draggedNodePos)
  let $dropTargetPos = tr.doc.resolve(dropPoint)

  let depth = (() => {
    if (mouseX === undefined || nodeRect === undefined) return undefined
    return calculateDepth(direction, $draggedPos, $dropTargetPos, mouseX - nodeRect.left)
  })()

  if (isValidDropTarget($draggedPos, $dropTargetPos, direction, depth) !== DropTargetType.valid) {
    dispatch(tr.setMeta(ALMANAC_DROP_CURSOR_PLUGIN_KEY, meta))
    return
  }

  if (
    [Direction.top, Direction.bottom].includes(direction) &&
    isDroppingNonListItemInsideList($draggedPos, $dropTargetPos)
  ) {
    // if we're performing a vertical drop, and we're dropping a non-list item inside a list
    if (depth !== 0) {
      // We're dropping as a child of a list item, so we need to make the drop target point to the
      // last child of the list item and force the direction to be bottom
      if ($dropTargetPos.index() > 0 && direction === Direction.top) {
        // we need to make $dropTargetPos point to the previous list item
        // nodeBefore is guaranteed to exist because we're not at the first list item (index > 0)
        $dropTargetPos = tr.doc.resolve($dropTargetPos.pos - $dropTargetPos.nodeBefore!.nodeSize)
      }
      const childCount = $dropTargetPos.nodeAfter?.childCount ?? 0
      const posOfLastChild = tr.doc.resolve($dropTargetPos.pos + 1).posAtIndex(childCount - 1)
      $dropTargetPos = tr.doc.resolve(posOfLastChild)
      direction = Direction.bottom
    } else if (depth === 0 && direction === Direction.top && $dropTargetPos.index() === 0) {
      // Edge case: we're dropping above the first list item at depth 0, so we change the target to
      // point to the list itself (instead of the first list item)
      $dropTargetPos = tr.doc.resolve($dropTargetPos.pos - 1)
    }
  }
  _applyDragMove(direction, $draggedPos, $dropTargetPos, tr, depth)

  tr.setMeta(ALMANAC_DROP_CURSOR_PLUGIN_KEY, meta)

  dispatch(tr)
  view.focus()

  trackEventInHeap(HeapEventType.SideStacking, { action: 'drop' })
}

export const handleMouseMove = (view: EditorView, event: MouseEvent) => {
  const pluginState = ALMANAC_DROP_CURSOR_PLUGIN_KEY.getState(view.state) as AlmanacDropCursorPluginState
  if (pluginState.isDragging) {
    event.preventDefault()
    let dragClone: HTMLElement | null
    if (event.buttons === 0) {
      handleDragEnd(view)
    } else {
      handleDragMove(view, event)
    }
    // eslint-disable-next-line no-cond-assign
    if ((dragClone = document.getElementById('dragClone'))) {
      const { x, y } = event
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const { x: PM_x, y: PM_y } = view.dom?.getBoundingClientRect()!

      dragClone.style.transform = `translate(${x - PM_x}px,${y - PM_y}px)`
    }

    return true
  }

  return false
}

const handleKeydown = (view: EditorView) => (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    const pluginState = ALMANAC_DROP_CURSOR_PLUGIN_KEY.getState(view.state) as AlmanacDropCursorPluginState
    if (pluginState.isDragging) {
      event.preventDefault()
      event.stopPropagation()
      view.dispatch(view.state.tr.setMeta(ALMANAC_DROP_CURSOR_PLUGIN_KEY, { action: 'stopDrag' }))
    }
  }
}

export let AlmanacDropCursorPlugin = new Plugin<AlmanacDropCursorPluginState, any>({
  key: ALMANAC_DROP_CURSOR_PLUGIN_KEY,

  state: {
    init: () => ({ isDragging: false }),
    apply(tr, pluginState) {
      const meta: AlmanacDropCursorPluginMeta = tr.getMeta(AlmanacDropCursorPlugin)
      if (!meta) {
        if (pluginState.dropPoint) {
          return { ...pluginState, dropPoint: tr.mapping.map(pluginState.dropPoint) }
        }
        return pluginState
      }
      const returnState = { ...pluginState }
      switch (meta.action) {
        case 'startDrag':
          document.documentElement.classList.add(styles.dragging)
          return {
            ...returnState,
            isDragging: true,
            draggedNodePos: meta.draggedNodePos,
            dropPoint: undefined,
            targetElementClone: meta.targetElementClone, // may be undefined, that's ok
          }
        case 'stopDrag':
          document.documentElement.classList.remove(styles.dragging)
          return { isDragging: false }
        case 'updateDropPoint':
          return {
            ...returnState,
            dropPoint: meta.dropPoint,
            direction: meta.direction,
            nodeRect: meta.nodeRect,
            mouseX: meta.mouseX,
            targetElementClone: meta.targetElementClone ?? pluginState.targetElementClone,
          }
        case 'updateMousePosOnly':
          return {
            ...returnState,
            mouseX: meta.mouseX,
          }
      }
    },
  },
  view: (view) => {
    document.addEventListener('keydown', handleKeydown(view))
    return {
      destroy: () => {
        document.documentElement.classList.remove(styles.dragging)
        document.removeEventListener('keydown', handleKeydown(view))
      },
    }
  },
  props: {
    decorations: (state: EditorState) => {
      const { isDragging, dropPoint, direction, nodeRect, draggedNodePos, targetElementClone, mouseX } =
        ALMANAC_DROP_CURSOR_PLUGIN_KEY.getState(state) as AlmanacDropCursorPluginState

      if (!isDragging || dropPoint === undefined || targetElementClone === undefined || draggedNodePos === undefined)
        return DecorationSet.empty

      const $dragPos = state.doc.resolve(draggedNodePos)
      const $dropPos = state.doc.resolve(dropPoint)
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const dropPointNode = state.doc.nodeAt(dropPoint)!

      let decoList = [Decoration.widget(0, targetElementClone, { key: 'mouseFollowerGhost' })]

      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      let decorationPos = [Direction.bottom, Direction.right].includes(direction!)
        ? dropPoint + dropPointNode.nodeSize
        : dropPoint

      if (dropPointNode.type === schema.nodes.column_group) {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        decorationPos += direction! === Direction.left ? 1 : -1
      }

      // the target being after the current node or before the node after current should be represented as the same
      // position, so we create a stable key (instead of simply using decoration)
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const directionPlane = [Direction.left, Direction.right].includes(direction!) ? 'horizontal' : 'vertical'

      if (direction === undefined) return DecorationSet.empty

      let depth = (() => {
        if (mouseX === undefined || nodeRect === undefined) return undefined
        return calculateDepth(direction, $dragPos, $dropPos, mouseX - nodeRect.left)
      })()

      const dropTargetType = isValidDropTarget($dragPos, $dropPos, direction, depth)

      if (dropTargetType !== DropTargetType.hidden) {
        /**
         * If it's a 2-column group and you're dragging the last element, show a full line as the preview, but only if
         * you're dropping to the left, otherwise the preview will overflow the last column and look weird. See EDI-1587
         * Defined as a function for legibility and because it only needs to be evaluated if we intend to draw as full col
         */
        const isFirstColumnOfGroup = () => $dropPos.node(-1).firstChild === $dropPos.node()
        const drawAsFullColumn = wouldResultInSingleColumn($dragPos, $dropPos) && isFirstColumnOfGroup()

        decoList.push(
          Decoration.widget(
            decorationPos,
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            createDropPointDecoEl(direction!, nodeRect!, {
              class: styles[dropTargetType],
              drawAsFullColumn,
              depth,
            }),
            { key: `dropPoint-${directionPlane}-${dropTargetType}-${depth}` },
          ),
        )
      }

      return DecorationSet.create(state.doc, decoList)
    },
    handleDOMEvents: {
      mouseup: (view: EditorView, event: Event) => {
        const mouseEvent = event as MouseEvent
        const pluginState = ALMANAC_DROP_CURSOR_PLUGIN_KEY.getState(view.state) as AlmanacDropCursorPluginState
        if (pluginState.isDragging) {
          mouseEvent.preventDefault()
          handleDragEnd(view)
          return true
        }
        return false
      },
      mousemove: handleMouseMove,
    },
  },
})

/**
 * Calculates the depth (indentation level) at which a drop operation would occur
 * @param direction The direction of the drop (only makes sense for `top` or `bottom`)
 * @param $dragPos The resolved position of the dragged node
 * @param $dropPos The resolved position of the target (drop point)
 * @param mouseOffsetFromLeft The offset of the mouse from the left of the target
 * @returns The depth (indentation level) at which the item would be dropped, or undefined if a
 * depth cannot be calculated (or doesn't make sense)
 */
export function calculateDepth(
  direction: Direction,
  $dragPos: ResolvedPos<any>,
  $dropPos: ResolvedPos<any>,
  mouseOffsetFromLeft: number,
) {
  // is the drop vertical (top or bottom)
  const isVerticalDrop = [Direction.top, Direction.bottom].includes(direction)
  // are we dragging a list item
  const isDraggedNodeListItem = isListItem($dragPos.nodeAfter?.type)
  // the index of the target drop point within its parent
  const dropPointIndex = $dropPos.index()
  // the node that represents the target drop point changes depending on whether we're dropping
  // above or below the target. Also, if we're dropping above the target, we need to check if the
  // target is the first item in the list, because in that case nodeBefore will be null
  const dropPointNodeToCheck =
    direction === Direction.top && dropPointIndex > 0 ? $dropPos.nodeBefore : $dropPos.nodeAfter
  // is the target node a list item?
  const isDropPointListItem = isVerticalDrop && isListItem(dropPointNodeToCheck?.type)
  // is the dragged node a list item? And are we dropping it inside a list?
  const isDroppingNonListItem = isDroppingNonListItemInsideList($dragPos, $dropPos)
  // if we're dropping inside a list
  if (isVerticalDrop && isDropPointListItem) {
    // edge case: if we're dropping above the first item in the list, it's not possible to drop as
    // a child of that item, so we return a depth of 0 (signifying that the item would be dropped
    // above the list)
    if (dropPointIndex === 0 && direction === Direction.top) {
      return 0
    }
    // we need to read the depth of the target node (at this point we know it's a list item)
    const nodeBeforeAttrs = dropPointNodeToCheck?.attrs as ExtractStrongNodeSpecAttributes<
      typeof nodes.list_item | typeof nodes.vote_list_item
    >
    // we calculate the desired depth based on how far to the left you move your mouse, in
    // 26px increments
    const desiredDropDepth = Math.floor(mouseOffsetFromLeft / 26)
    // then we clamp the desired depth so it can go no deeper than the target node's depth + 1
    // this is different from using the keyboard to (un)indent, where you can go as deep as you want
    const clampedDropDepth = clamp(desiredDropDepth, 0, Math.min(nodeBeforeAttrs.depth + 1, 10))
    if (isDraggedNodeListItem) {
      // if we're dragging a list item, we are done, and can return the clamped depth
      return clampedDropDepth
    } else if (isDroppingNonListItem) {
      // if we're dropping a non-list-item, we have two choices:
      // 1. Drop it as a child of the list item, after the existing content (in which case we return
      //    the depth of the list item + 1, indicating that it should be a child of the list item)
      // 2. Break up the list, and drop the non-list-item in such a way as to bisect the list
      //    or before/after the list if you're targeting above the first list item or below the last
      if (clampedDropDepth === 0 || clampedDropDepth < nodeBeforeAttrs.depth) {
        // If the clamped depth is 0 or less than the target node's depth, we're breaking up the
        // list (or dropping above/below the list), so we return 0
        // We have two checks here because specifically at depth 0 you need to move the mouse a bit
        // further to the right if you want to indicate you want to drop as a child of the list item
        return 0
      } else {
        // Otherwise, we presume you're hovering over the list item, and want to drop the non-list
        // item as a child of the list item, so we return the target node's depth + 1 (note we don't
        // use the clamped depth here, that's only for determining whether to break up the list or
        // drop as a child)
        return nodeBeforeAttrs.depth + 1
      }
    }
  }
}
